import React, { useState, useEffect } from "react";
import { IEvent, IShow, IUkcObedienceAwards } from "@eagerdog/interfaces";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";
import { calculateUkcObedienceAwards } from "@eagerdog/awards-util";

import "./UkcObedienceAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: any[];
}

const UkcObedienceAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IUkcObedienceAwards | null>(null);

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      // Filter scores for this show
      const showScores = scores.filter(
        (s) =>
          s.show_type === show.show_type &&
          s.show_id.toString() === show._id.toString()
      );

      const calculatedAwards = calculateUkcObedienceAwards(showScores);
      setAwards(calculatedAwards);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="UkcObedienceAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="UkcObedienceAwards error">Failed to calculate awards</div>;
  }

  return (
    <div className="UkcObedienceAwards">
      {/* High in Trial */}
      <div className="awardSection">
        <h4>High in Trial</h4>
        {awards.highInTrial ? (
          <p className="awardWinner">
            #{awards.highInTrial.arm_number} {awards.highInTrial.registered_name} ({awards.highInTrial.breed}) – 
            {awards.highInTrial.level} – Score: {awards.highInTrial.score}
          </p>
        ) : (
          <p className="noWinner">No qualifying scores for this award (minimum score 195 required)</p>
        )}
      </div>

      {/* High in Trial Junior */}
      <div className="awardSection">
        <h4>High in Trial Junior</h4>
        {awards.highInTrialJunior ? (
          <p className="awardWinner">
            #{awards.highInTrialJunior.arm_number} {awards.highInTrialJunior.registered_name} ({awards.highInTrialJunior.breed}) – 
            {awards.highInTrialJunior.level} – Score: {awards.highInTrialJunior.score}
          </p>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>

      {/* High Combined */}
      <div className="awardSection">
        <h4>High Combined (Open B + Utility A or Utility B)</h4>
        {awards.highCombined ? (
          <>
            <p className="awardWinner">
              #{awards.highCombined.arm_number} {awards.highCombined.registered_name} ({awards.highCombined.breed}) – 
              Combined Score: {awards.highCombined.combined_score}
            </p>
            <p className="awardDetail">
              Classes: {awards.highCombined.levels.join(", ")}
            </p>
          </>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>
    </div>
  );
};

export default UkcObedienceAwards;

